import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";

import {
  Avatar,
  Card,
  CardBody,
  Icon,
  type IconName,
  Modal,
  PageHead,
  Segmented,
  Toggle,
} from "@/components/common";
import { useAuth } from "@/auth/useAuth";
import { useTheme, type ThemeSetting } from "@/contexts/ThemeContext";
import { authService } from "@/services/authService";
import { useRevokeOtherSessions } from "@/hooks/useSessions";
import { ConsentsPanel } from "./ConsentsPanel";
import { MfaManager } from "./MfaManager";
import { RecentActivity } from "./RecentActivity";
import { SessionsList } from "./SessionsList";
import "./Preferences.css";

type Density = "compact" | "comfortable" | "spacious";
type Locale = "pt-BR" | "en" | "es";

type ActionCardProps = {
  icon: IconName;
  title: string;
  desc: string;
  actionLabel: string;
  onAction?: () => void;
  danger?: boolean;
};

/** Card with an icon, title, description and a bottom action link (↗). When
 * `onAction` is omitted the action shows as "Em breve" (not yet wired). */
function ActionCard({ icon, title, desc, actionLabel, onAction, danger }: ActionCardProps) {
  return (
    <div className={`pref-card ${danger ? "is-danger" : ""}`}>
      <div className="pref-card-icon">
        <Icon name={icon} size={18} />
      </div>
      <div className="pref-card-title">{title}</div>
      <p className="pref-card-desc">{desc}</p>
      {onAction ? (
        <button type="button" className="pref-card-action" onClick={onAction}>
          {actionLabel}
          <Icon name="arrow-up-right" size={14} />
        </button>
      ) : (
        <span className="pref-card-action is-soon">Em breve</span>
      )}
    </div>
  );
}

export function PreferencesPage() {
  const { user } = useAuth();
  const { setting, setSetting } = useTheme();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const revokeOthers = useRevokeOtherSessions();

  const [mfaOpen, setMfaOpen] = useState(false);
  const [devicesOpen, setDevicesOpen] = useState(false);

  const activeWs = user?.manageableWorkspaces.find((w) => w.id === user.activeWorkspaceId);
  const role = user?.isPlatformAdmin ? `TDAX · ${user.platformRoleSlug ?? "admin"}` : "Membro";

  async function logoutEverywhere() {
    await revokeOthers.mutateAsync().catch(() => undefined);
    await authService.logout();
    qc.clear();
    navigate("/login");
  }

  return (
    <>
      <PageHead
        title="Preferências"
        subtitle="Suas preferências pessoais — conta, notificações, aparência e sessões"
      />

      {/* VOCÊ */}
      <Card>
        <CardBody>
          <div className="pref-hero">
            <Avatar name={user?.name} size="lg" />
            <div className="pref-hero-main">
              <span className="pref-hero-eyebrow">Você</span>
              <h2 className="pref-hero-name">{user?.name ?? "—"}</h2>
              <span className="pref-hero-mail">{user?.email}</span>
            </div>
            <div className="pref-hero-stats">
              <div>
                <span className="pref-stat-label">Workspace atual</span>
                <span className="pref-stat-value">{activeWs?.name ?? "—"}</span>
              </div>
              <div>
                <span className="pref-stat-label">Papel</span>
                <span className="pref-stat-value">{role}</span>
              </div>
            </div>
          </div>
        </CardBody>
      </Card>

      {/* CONTA */}
      <div className="pref-section-label">Conta</div>
      <div className="pref-grid">
        <ActionCard
          icon="user"
          title="Perfil pessoal"
          desc="Nome, foto, telefone, idioma e fuso horário."
          actionLabel="Editar"
        />
        <ActionCard
          icon="mail"
          title="E-mail principal"
          desc={`${user?.email ?? "—"}${user?.emailVerified ? " — verificado." : " — não verificado."}`}
          actionLabel="Trocar e-mail"
        />
        <ActionCard
          icon="shield-check"
          title="Senha e 2FA"
          desc={`Autenticação em dois fatores ${user?.mfaEnabled ? "ativa" : "inativa"}.`}
          actionLabel="Gerenciar"
          onAction={() => setMfaOpen(true)}
        />
        <ActionCard
          icon="phone"
          title="Sessões ativas"
          desc="Dispositivos conectados à sua conta e histórico de acessos."
          actionLabel="Ver dispositivos"
          onAction={() => setDevicesOpen(true)}
        />
      </div>

      {/* NOTIFICAÇÕES + APARÊNCIA */}
      <div className="pref-two-col">
        <div>
          <div className="pref-section-label">
            Notificações
            <span className="pref-section-soon">Em breve</span>
          </div>
          {/* Sem backend de preferências de notificação ainda — controles desabilitados
              até existir o endpoint de persistência. Não usar estado local fake. */}
          <Card>
            <CardBody className="pref-toggles">
              <Toggle
                checked
                disabled
                onChange={() => undefined}
                label="E-mail diário"
                sub="Resumo das atividades do dia anterior, enviado às 7h."
              />
              <Toggle
                checked
                disabled
                onChange={() => undefined}
                label="Push no navegador"
                sub="Notificações instantâneas para menções e alertas críticos."
              />
              <Toggle
                checked={false}
                disabled
                onChange={() => undefined}
                label="SMS"
                sub="Apenas para alertas marcados como críticos (cobrança, vencimentos)."
              />
              <Toggle
                checked={false}
                disabled
                onChange={() => undefined}
                label="Slack pessoal"
                sub="Integração de notificações via Slack."
              />
            </CardBody>
          </Card>
        </div>

        <div>
          <div className="pref-section-label">Aparência</div>
          <Card>
            <CardBody className="pref-appearance">
              <div className="pref-appearance-row">
                <div>
                  <div className="pref-row-title">Tema</div>
                  <p className="pref-row-sub">Alterna entre claro, escuro ou seguir o sistema.</p>
                </div>
                <Segmented<ThemeSetting>
                  ariaLabel="Tema"
                  value={setting}
                  onChange={setSetting}
                  options={[
                    { value: "light", label: "Claro" },
                    { value: "dark", label: "Escuro" },
                    { value: "auto", label: "Auto" },
                  ]}
                />
              </div>
              <div className="pref-appearance-row">
                <div>
                  <div className="pref-row-title">
                    Densidade da interface
                    <span className="pref-section-soon">Em breve</span>
                  </div>
                  <p className="pref-row-sub">Confortável é o padrão. Compacto reduz espaçamento.</p>
                </div>
                {/* Sem persistência ainda — opções desabilitadas até existir o backend. */}
                <Segmented<Density>
                  ariaLabel="Densidade"
                  value="comfortable"
                  onChange={() => undefined}
                  options={[
                    { value: "compact", label: "Compacto", disabled: true },
                    { value: "comfortable", label: "Confortável", disabled: true },
                    { value: "spacious", label: "Espaçoso", disabled: true },
                  ]}
                />
              </div>
              <div className="pref-appearance-row">
                <div>
                  <div className="pref-row-title">
                    Idioma da interface
                    <span className="pref-section-soon">Em breve</span>
                  </div>
                  <p className="pref-row-sub">Aplica em todo o app, e-mails e relatórios gerados.</p>
                </div>
                {/* Sem persistência ainda — apenas pt-BR disponível. */}
                <Segmented<Locale>
                  ariaLabel="Idioma"
                  value="pt-BR"
                  onChange={() => undefined}
                  options={[
                    { value: "pt-BR", label: "pt-BR", disabled: true },
                    { value: "en", label: "EN", disabled: true },
                    { value: "es", label: "ES", disabled: true },
                  ]}
                />
              </div>
            </CardBody>
          </Card>
        </div>
      </div>

      {/* AVANÇADO */}
      <div className="pref-section-label">Avançado</div>
      <div className="pref-grid pref-grid-3">
        <ActionCard
          icon="file-text"
          title="Exportar meus dados"
          desc="Baixe um ZIP com perfil, organizações em que você é responsável e atividades."
          actionLabel="Solicitar export"
        />
        <ActionCard
          icon="log-out"
          title="Sair de todos os dispositivos"
          desc="Encerra todas as sessões ativas. Você precisará fazer login novamente."
          actionLabel="Encerrar sessões"
          onAction={() => void logoutEverywhere()}
        />
        <ActionCard
          icon="alert-triangle"
          title="Encerrar minha conta"
          desc="Apaga seu perfil pessoal. Workspaces em que você é admin único precisam ser transferidos antes."
          actionLabel="Iniciar processo"
          danger
        />
      </div>

      {/* PRIVACIDADE (LGPD) */}
      <div className="pref-section-label">Privacidade</div>
      <ConsentsPanel />

      {mfaOpen && (
        <Modal title="Senha e autenticação" size="sm" onClose={() => setMfaOpen(false)}>
          <MfaManager />
        </Modal>
      )}

      {devicesOpen && (
        <Modal
          title="Sessões ativas e acessos"
          subtitle="Dispositivos conectados e histórico recente de autenticação"
          size="lg"
          onClose={() => setDevicesOpen(false)}
        >
          <SessionsList />
          <RecentActivity />
        </Modal>
      )}
    </>
  );
}
